// components/ARViewer.jsx
import React, { Suspense, useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { XR, useXR, ARButton, useHitTest } from '@react-three/xr';
import { OrbitControls, useGLTF, Environment, Html } from '@react-three/drei';
import { Button, Box, Dialog, CircularProgress, Typography, IconButton } from '@mui/material';
import { Close as CloseIcon, TouchApp as TouchIcon } from '@mui/icons-material';

// 3D Model Component
const ProductModel = ({ modelPath, scale = 0.5, position = [0, 0, 0] }) => {
  const { scene } = useGLTF(modelPath);
  const modelRef = useRef();
  
  useEffect(() => {
    if (scene) {
      const box = new THREE.Box3().setFromObject(scene);
      const center = box.getCenter(new THREE.Vector3());
      
      scene.position.x = -center.x;
      scene.position.y = -center.y;
      scene.position.z = -center.z;
    }
  }, [scene]);
  
  return <primitive ref={modelRef} object={scene} scale={scale} position={position} />;
};

// AR Content Component
const ARContent = ({ modelPath, onModelPlaced, language }) => {
  const { isPresenting } = useXR();
  const [modelPosition, setModelPosition] = useState([0, -1, -1.5]);
  const [isPlaced, setIsPlaced] = useState(false);
  const hitTestResults = useRef([]);
  
  // Hit test to place model on detected surface
  useHitTest((hitMatrix, hit) => {
    if (!isPlaced && hit && hitMatrix) {
      const position = [hitMatrix[12], hitMatrix[13], hitMatrix[14]];
      setModelPosition([position[0], position[1] + 0.1, position[2]]);
      setIsPlaced(true);
      onModelPlaced?.(true);
    }
  });
  
  // Auto-center model when entering AR
  useEffect(() => {
    if (isPresenting && !isPlaced) {
      const timeout = setTimeout(() => {
        if (!isPlaced) {
          setModelPosition([0, -0.5, -1.5]);
          setIsPlaced(true);
          onModelPlaced?.(true);
        }
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isPresenting]);
  
  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[5, 5, 5]} intensity={1} />
      <directionalLight position={[-5, 5, 0]} intensity={0.5} />
      <pointLight position={[0, 2, 2]} intensity={0.5} />
      
      <group position={modelPosition}>
        <ProductModel modelPath={modelPath} scale={0.4} />
      </group>
      
      {isPresenting && !isPlaced && (
        <Html center position={[0, 1, -1]}>
          <Box sx={{
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: '#FFD700',
            px: 2,
            py: 1,
            borderRadius: '30px',
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
            backdropFilter: 'blur(8px)',
            border: '1px solid #FFD700',
          }}>
            <TouchIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
            {language === 'ar' ? 'حرك الهاتف لوضع المنتج' : 'Move phone to place product'}
          </Box>
        </Html>
      )}
      
      <Environment preset="studio" />
    </>
  );
};

// Non-AR 3D Viewer Component
const NonARViewer = ({ modelPath, autoRotate = true }) => {
  const { scene } = useGLTF(modelPath);
  const modelRef = useRef();
  
  useEffect(() => {
    if (scene) {
      const box = new THREE.Box3().setFromObject(scene);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = maxDim > 1.5 ? 1.5 / maxDim : 0.8;
      
      scene.scale.set(scale, scale, scale);
      scene.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
    }
  }, [scene]);
  
  useFrame((state, delta) => {
    if (autoRotate && modelRef.current) {
      modelRef.current.rotation.y += delta * 0.5;
    }
  });
  
  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 5, 5]} intensity={1} />
      <directionalLight position={[-5, 5, 0]} intensity={0.5} />
      <pointLight position={[0, 2, 2]} intensity={0.5} />
      <spotLight position={[0, 3, 2]} angle={0.3} intensity={0.5} />
      
      <primitive ref={modelRef} object={scene} />
      
      <Environment preset="studio" />
      <OrbitControls
        enableZoom={false}
        enablePan={false}
        enableRotate={true}
        rotateSpeed={1.5}
        target={[0, 0, 0]}
      />
    </>
  );
};

// Main AR Viewer Component
const ARViewer = ({ open, onClose, product, language }) => {
  const [isARSupported, setIsARSupported] = useState(false);
  const [showAR, setShowAR] = useState(false);
  const [modelPlaced, setModelPlaced] = useState(false);
  const [isXREnabled, setIsXREnabled] = useState(false);

  useEffect(() => {
    const checkARSupport = async () => {
      if (navigator.xr) {
        try {
          const supported = await navigator.xr.isSessionSupported('immersive-ar');
          setIsARSupported(supported);
        } catch (err) {
          console.error('AR not supported:', err);
          setIsARSupported(false);
        }
      } else {
        setIsARSupported(false);
      }
    };
    
    if (open) {
      checkARSupport();
      setShowAR(false);
      setModelPlaced(false);
      setIsXREnabled(false);
    }
  }, [open]);

  const handleClose = () => {
    setShowAR(false);
    onClose();
  };

  const handleARButtonClick = () => {
    setShowAR(true);
    // Force re-render to initialize XR
    setIsXREnabled(true);
  };

  if (!product?.ar_model_path) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: '#000000',
          position: 'relative',
          m: 0,
          width: '100%',
          height: '100%',
        },
      }}
    >
      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        {/* Controls */}
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {isARSupported && !showAR && (
            <Button
              variant="contained"
              onClick={handleARButtonClick}
              startIcon={<TouchIcon />}
              sx={{
                backgroundColor: '#4CAF50',
                color: '#FFFFFF',
                borderRadius: '8px',
                padding: '8px 16px',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: '#45a049' },
              }}
            >
              {language === 'ar' ? 'عرض في الواقع المعزز' : 'View in AR'}
            </Button>
          )}
          
          {showAR && (
            <Button
              variant="outlined"
              onClick={() => {
                setShowAR(false);
                setIsXREnabled(false);
              }}
              sx={{
                borderColor: '#FF3D00',
                color: '#FF3D00',
                borderRadius: '8px',
                '&:hover': { backgroundColor: 'rgba(255, 61, 0, 0.1)' },
              }}
            >
              {language === 'ar' ? 'خروج من AR' : 'Exit AR'}
            </Button>
          )}
          
          <IconButton
            onClick={handleClose}
            sx={{
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.2)', color: '#FFD700' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Instructions */}
        {!showAR && (
          <Box sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: '#FFFFFF',
            px: 2,
            py: 1,
            borderRadius: '30px',
            fontSize: '0.75rem',
            textAlign: 'center',
            backdropFilter: 'blur(8px)',
            whiteSpace: { xs: 'normal', sm: 'nowrap' },
            maxWidth: '90%',
          }}>
            {language === 'ar'
              ? 'اسحب الشاشة لتدوير المنتج 360 درجة'
              : 'Drag to rotate the product 360 degrees'}
          </Box>
        )}

        {showAR && !modelPlaced && (
          <Box sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: '#FFD700',
            px: 2,
            py: 1,
            borderRadius: '30px',
            fontSize: '0.75rem',
            textAlign: 'center',
            backdropFilter: 'blur(8px)',
            whiteSpace: { xs: 'normal', sm: 'nowrap' },
            maxWidth: '90%',
          }}>
            {language === 'ar'
              ? 'حرك الهاتف ببطء لوضع المنتج على الأرض'
              : 'Move your phone slowly to place the product on the floor'}
          </Box>
        )}

        <Suspense
          fallback={
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}>
              <CircularProgress sx={{ color: '#FFD700' }} />
              <Typography sx={{ color: '#FFFFFF', mt: 2 }}>
                {language === 'ar' ? 'جاري تحميل النموذج...' : 'Loading 3D model...'}
              </Typography>
            </Box>
          }
        >
          {showAR ? (
            // AR Mode - Wrap Canvas with ARButton directly
            <div style={{ height: '100%', width: '100%', position: 'relative' }}>
              <ARButton
                sessionInit={{ requiredFeatures: ['local-floor', 'hit-test'] }}
                style={{
                  position: 'absolute',
                  bottom: 20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 20,
                  backgroundColor: '#FFD700',
                  color: '#000000',
                  border: 'none',
                  borderRadius: '30px',
                  padding: '12px 24px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px',
                }}
              >
                {language === 'ar' ? 'تشغيل الواقع المعزز' : 'Start AR Experience'}
              </ARButton>
              <Canvas
                camera={{ position: [0, 0, 2], fov: 45 }}
                style={{ backgroundColor: '#000000', height: '100%', width: '100%' }}
              >
                <XR>
                  <ARContent 
                    modelPath={product.ar_model_path} 
                    onModelPlaced={(placed) => setModelPlaced(placed)}
                    language={language}
                  />
                </XR>
              </Canvas>
            </div>
          ) : (
            // Non-AR 3D Viewer
            <Canvas
              camera={{ position: [0, 0, 2], fov: 45 }}
              style={{ backgroundColor: '#0D0D0D', height: '100%', width: '100%' }}
              onCreated={() => {}}
            >
              <color attach="background" args={['#0D0D0D']} />
              <NonARViewer modelPath={product.ar_model_path} autoRotate={true} />
            </Canvas>
          )}
        </Suspense>
        
        {!isARSupported && !showAR && (
          <Box sx={{
            position: 'absolute',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: '#FFD700',
            px: 2,
            py: 1,
            borderRadius: '8px',
            fontSize: '0.75rem',
            textAlign: 'center',
            maxWidth: '80%',
          }}>
            {language === 'ar' 
              ? 'عذراً، الواقع المعزز غير مدعوم على هذا الجهاز.'
              : 'Sorry, AR is not supported on this device.'}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default ARViewer;